import { useRefract } from "refract-rxjs";

import { type ApolloResult } from "../../shared/models";
import {
    type DimensionBreakdownFunnelInput,
    type DimensionBreakdownFunnelOutput,
    dimensionBreakdownFunnelEffect,
} from "../effects/dimensions-breakdown-funnel";
import {
    createFieldListWithDimensions,
    funnelAnalysisDimensions,
    funnelAnalysisFieldsWithDimensionsCascadingOrder,
} from "../models";
import { type DimensionFunnelMap } from "../models/dimension-funnel";

import { useChartsTotalQuery } from "./useChartsTotalQuery";
import { useDimensionBreakdownQuery } from "./useDimensionBreakdownQuery";
import { useDimensionBreakdownQueryParams } from "./useDimensionBreakdownQueryParams";
import { useTimeRange } from "./useTimeRange";

const fields = createFieldListWithDimensions(
    funnelAnalysisDimensions,
    funnelAnalysisFieldsWithDimensionsCascadingOrder,
);

export const useDimensionBreakdownFunnel = (): ApolloResult<{
    dimensionFunnelMap: DimensionFunnelMap;
    isEmpty: boolean;
}> => {
    const { isAllTimeSelected, selectedTimeRange } = useTimeRange();

    const dimensionBreakdownParams = useDimensionBreakdownQueryParams(fields);
    const dimensionBreakdownTotalParams = useDimensionBreakdownQueryParams(fields);

    const {
        data: dimensionBreakdownData,
        error: dimensionBreakdownError,
        loading: dimensionBreakdownLoading,
    } = useDimensionBreakdownQuery(dimensionBreakdownParams);

    const {
        data: dimensionBreakdownTotalData,
        error: dimensionBreakdownTotalError,
        loading: dimensionBreakdownTotalLoading,
    } = useChartsTotalQuery({
        ...dimensionBreakdownTotalParams,
        isAllTimeSelected,
        selectedTimeRange,
        fields: funnelAnalysisFieldsWithDimensionsCascadingOrder,
    });

    const { dimensionFunnelMap, isEmpty } = useRefract<DimensionBreakdownFunnelInput, DimensionBreakdownFunnelOutput>(
        dimensionBreakdownFunnelEffect,
        {
            dimensionBreakdownData,
            dimensionBreakdownLoading,
            dimensionBreakdownTotalData,
            dimensionBreakdownTotalLoading,
        },
    );

    return {
        loading: dimensionBreakdownLoading || dimensionBreakdownTotalLoading,
        error: dimensionBreakdownError ?? dimensionBreakdownTotalError,
        dimensionFunnelMap,
        isEmpty,
    };
};
